<template>
  <div>
    <provisioning-qos-status @refresh="load" qos-type="CLOUD_QOS" />
    <v-card class="main-card">
      <v-card-title class="screen-title">
        <span>クラウド向けQoS一覧</span>
        <manual-button path="/cloud-qos/" />
      </v-card-title>
      <v-card-text class="pa-2" v-if="isLoaded">
        <v-row no-gutters>
          <v-col>
            <app-search :search="cloudQos.search" v-model="cloudQos.filter" />
          </v-col>
          <div class="d-flex flex-row ga-1 flex-wrap ml-2">
            <v-btn :href="downloadUrl" color="primary">一覧ダウンロード</v-btn>
          </div>
        </v-row>
        <div class="d-flex flex-row justify-end mb-2">
          <v-btn color="primary" @click="load">一覧更新</v-btn>
        </div>
        <app-table
          v-model:table-sort-by="cloudQos.sortBy"
          primary-key="vpnVnCode"
          :fields="cloudQos.fields"
          :items="cloudQos.items"
          :filterOption="cloudQos.filter_option"
          :filter="cloudQos.filter"
          v-model="cloudQos.selected"
          :hover="true"
          sticky-header="50vh"
          @click:row="showDetails"
        >
          <!-- 回線数 -->
          <template #cell(line)="data">
            <template v-if="data.item.vpnVnCode.startsWith('B')">
              {{ data.item.numberOfVnConnect }}
            </template>
            <template v-else>
              {{
                `${data.item.numberOfVnConnect}/${data.item.upperLimitVnConnect}`
              }}
            </template>
          </template>
          <!-- 仮想ネットワーク名 -->
          <template #cell(vnName)="{ value }">
            {{ $filter.emptyTo(value, "-") }}
          </template>
          <!-- 設定可否情報 -->
          <template #cell(qosControl)="data">
            {{ $filter.enumTo(data.item.qosControl, "qosControl") }}
          </template>
        </app-table>
      </v-card-text>
    </v-card>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import type { CloudQosInfo } from "@/apis/CloudQosApi";
import CloudQosViewRC from "@/modals/cloudQos/CloudQosViewRC.vue";
import type { SearchOption } from "@/components/AppSearch.vue";
import type {
  AppTableHeader,
  FilterOption,
  SortItem,
} from "@/components/AppTable.vue";

export default defineComponent({
  name: "CloudQosSettingList",
  data() {
    return {
      cloudQos: {
        sortBy: { key: "vpnVnCode", order: "desc" } as SortItem,
        fields: [
          {
            key: "vpnVnCode",
            title: "VPN/VNコード",
            width: "150px",
            sortable: true,
          },
          {
            key: "vnName",
            title: "仮想ネットワーク名",
            width: "200px",
            sortable: true,
            sortByFormatted: true,
            filterByFormatted: true,
            formatter: (value: string, key: null, item: CloudQosInfo) => {
              return item.vpnVnCode.startsWith("B") ? null : value;
            },
          },
          {
            key: "qosType",
            title: "QoS方式",
            width: "130px",
            sortable: true,
            sortByFormatted: true,
            filterByFormatted: false,
            formatter: (value: string) => {
              if (value === "NONE") {
                return "-";
              } else {
                return this.$filter.enumTo(value, "qosType");
              }
            },
          },
          {
            key: "priorityTarget",
            title: "優先度参照先",
            width: "180px",
            sortable: true,
            sortByFormatted: true,
            filterByFormatted: false,
            formatter: (value: string) => {
              if (value === "NONE") {
                return "-";
              } else {
                return this.$filter.enumTo(value, "cloudQos_PriorityTarget");
              }
            },
          },
          { key: "line", title: "回線数", width: "80px" },
          {
            key: "qosControl",
            title: "設定可/不可",
            width: "120px",
            align: "center",
          },
        ] as AppTableHeader<CloudQosInfo>[],
        items: [] as CloudQosInfo[],
        search: {
          vpnVnCode: { type: "text", label: "VPN/VNコード" },
          vnName: { type: "text", label: "仮想ネットワーク名" },
          qosType: {
            type: "select",
            label: "QoS方式",
            items: [
              { value: "ABSOLUTE", text: "絶対優先方式" },
              { value: "BANDWIDTH", text: "帯域制御方式" },
              { value: "NONE", text: "-" },
            ],
          },
          priorityTarget: {
            type: "select",
            label: "優先度参照先",
            items: [
              { value: "IPPRECEDENCE_DSCP", text: "IP Precedence/DSCP" },
              { value: "DSCP", text: "DSCP" },
              { value: "NONE", text: "-" },
            ],
          },
        } as SearchOption,
        filter: {} as Record<string, unknown>,
        filter_option: {
          vpnVnCode: "like",
          vnName: "like",
          qosType: "equal",
          priorityTarget: "equal",
        } as FilterOption,
        selected: [] as CloudQosInfo[],
      },
      // 初期通信完了フラグ
      isLoaded: false,
      // download用URL
      downloadUrl: this.$api.cloudQosApi.getCsvCloudQosURL(),
    };
  },
  async mounted() {
    await this.load();
    this.isLoaded = true;
  },
  methods: {
    async load() {
      this.cloudQos.items = await this.$api.cloudQosApi.getCloudQosInfoList();
    },
    async showDetails(item: CloudQosInfo) {
      const cloudQosDetail = await this.$api.cloudQosApi.getCloudQosDetail(
        item.vpnVnCode
      );
      await this.$modal.show(CloudQosViewRC, {
        vpnVnCode: item.vpnVnCode,
        vnName: item.vnName,
        isNew: cloudQosDetail.qosType === "NONE",
        mode: "E",
        cloudQosDetail: cloudQosDetail,
      });
      await this.load();
    },
  },
});
</script>
